/**
 * Microsoft Graph helper — read the members of a specific Entra group using the
 * SIGNED-IN USER's delegated token. Powers the Field Leader picker: the "Field
 * Leaders" group is the source of truth for who can be assigned as a field
 * leader, so the picker only ever offers real group members.
 *
 * Scopes (both required): GroupMember.Read.All to enumerate the membership, and
 * User.ReadBasic.All to read each member's displayName/mail — with the former
 * alone, members return with id only and null profile fields.
 *
 * Mirrors graphUpload.ts: a token that can't be acquired surfaces as a
 * GraphConsentError so the UI can show a friendly "still being set up" message.
 */
import { GraphConsentError } from './graphUpload';

const GRAPH = 'https://graph.microsoft.com/v1.0';

export interface GroupMember {
  id: string;            // Entra (Azure AD) object id
  displayName: string;
  email: string | null;
}

/**
 * All user members of the group (groups are small for field leaders, so we pull
 * the page and filter client-side). The `/microsoft.graph.user` cast restricts
 * to users — nested groups / service principals are skipped.
 */
export async function fetchGroupMembers(
  getToken: () => Promise<string>,
  groupId: string,
): Promise<GroupMember[]> {
  const token = await getToken();
  const url =
    `${GRAPH}/groups/${groupId}/members/microsoft.graph.user` +
    `?$select=id,displayName,mail,userPrincipalName&$top=999`;
  const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) {
    if (res.status === 401 || res.status === 403) {
      throw new GraphConsentError('Graph consent required for group read');
    }
    throw new Error(`Graph group members request failed: ${res.status}`);
  }
  const data = await res.json();
  return ((data.value ?? []) as any[]).map((m) => ({
    id: m.id as string,
    displayName: (m.displayName ?? m.userPrincipalName ?? '(unknown)') as string,
    email: (m.mail ?? m.userPrincipalName ?? null) as string | null,
  }));
}
